import Button from '@ui/buttons/button/button';
import { memo, useCallback } from 'react';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import { TRequiresTimeChange } from '@/types/session.types';
import timezoneMap from '../timezone-short-name-map.json' with { type: 'json' };
import { getDateOptionsDetail } from '../incremental-options-utils/get-options-detail';

type DateRangePickerIncrementalOptionsProps = {
  startDate: Date | null;
  endDate: Date | null;
  isEnd?: boolean;
  generatedOptionIncrementMinutes?: number;
  requiresTimeChange?: TRequiresTimeChange | null;
  timeZone?: keyof typeof timezoneMap | undefined;
  onSelect?: (value: Date, isEnd: boolean) => void;
};

const DateRangePickerIncrementalOptions = memo(
  ({
    startDate,
    endDate,
    isEnd = false,
    generatedOptionIncrementMinutes = 30,
    requiresTimeChange,
    timeZone,
    onSelect,
  }: DateRangePickerIncrementalOptionsProps) => {
    const generateTimeSelectOptions = useCallback(() => {
      const getOptionJSX = (optionDetails: {
        value: Date;
        label: string;
        daysDiff: number | null;
        hoursDiff: number | null;
        minutesDiff: number | null;
      }) => {
        return (
          <Button
            clear
            className="flex fd-row cursor-pointer"
            key={optionDetails.value.toString()}
            padding="8px 12px"
            hoverBackgroundColor="var(--gray-700)"
            width="100%"
            onClick={() => {
              if (onSelect) {
                onSelect(optionDetails.value, !!isEnd);
              }
            }}
          >
            <Text className="S2" width="64px">
              {optionDetails.label}
            </Text>
            <Text className="S2" color="var(--gray-600)">
              {`${optionDetails.daysDiff ? optionDetails.daysDiff + 'd ' : ''}${optionDetails.hoursDiff ? optionDetails.hoursDiff + 'h ' : ''}${optionDetails.minutesDiff ? optionDetails.minutesDiff + 'm' : ''}`}
            </Text>
          </Button>
        );
      };

      const getNoOptionJSX = () => {
        return (
          <Container className="flex fd-row cursor-pointer" padding="8px 12px" width="100%">
            <Text className="S2" color="var(--gray-600)">
              -- No options available --
            </Text>
          </Container>
        );
      };

      const dateOptionsDetails = getDateOptionsDetail(
        startDate,
        endDate,
        timeZone,
        isEnd,
        generatedOptionIncrementMinutes,
        requiresTimeChange
      );

      if (!dateOptionsDetails.length) {
        return getNoOptionJSX();
      }

      return dateOptionsDetails.map((optionDetails) => {
        return getOptionJSX(optionDetails);
      });
    }, [endDate, generatedOptionIncrementMinutes, isEnd, onSelect, requiresTimeChange, startDate, timeZone]);

    return <>{generateTimeSelectOptions()}</>;
  }
);

export default DateRangePickerIncrementalOptions;
